// Inside your CouponsPanel.js component
import React, { useState, useEffect } from 'react';
import { WEB_APP_URL, SECRET_TOKEN } from '../config';
import LoadingOverlay from './LoadingOverlay';
import RedeemSliderModal from './RedeemSliderModal';
import coupon from '../assets/coupon.png';
import { toast } from 'react-toastify';

// Cache helpers
const getCache = (key) => {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
};
const setCache = (key, data) => {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
};

const CouponsPanel = ({ refreshCouponsTrigger, onCouponUsed }) => {
  const [coupons, setCoupons] = useState(() => getCache('coupons') ?? 0);
  const [loading, setLoading] = useState(getCache('coupons') === null);
  const [showSlider, setShowSlider] = useState(false);

  const fetchCoupons = async () => {
    try {
      const response = await fetch(`${WEB_APP_URL}?action=getCoupons`, { redirect: "follow" });
      const result = await response.json();
      setCoupons(result.coupons || 0);
      setCache('coupons', result.coupons || 0);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (getCache('coupons') === null) setLoading(true);
    fetchCoupons();
  }, [refreshCouponsTrigger]);

  const handleRedeemClick = () => {
    if (coupons <= 0) {
      toast.error("No coupons remain", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }
    setShowSlider(true);
  };

  const handleConfirmRedeem = async () => {
    setShowSlider(false);
    
    // Show an immediate loading toast.
    const toastId = toast.loading("Redeeming coupon...");
    
    setLoading(true);
    try {
      const response = await fetch(WEB_APP_URL, {
        method: 'POST',
        redirect: 'follow',
        headers: {
          "Content-Type": "text/plain;charset=utf-8"
        },
        body: JSON.stringify({
          action: "useCoupon",
          token: SECRET_TOKEN
        }),
      });
      const result = await response.json();
      if (result.success) {
        onCouponUsed(); // Notify parent to refresh coupon count
        toast.update(toastId, {
          render: "Coupon Redeemed! Enjoy your new book 📚",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      } else {
        toast.update(toastId, {
          render: "Failed to redeem coupon",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        setLoading(false);
      }
    } catch (error) {
      console.error("Error using coupon:", error);
      toast.update(toastId, {
        render: "Error using coupon",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      setLoading(false);
    }
  };

  const hasCachedData = coupons !== null;
  
  return (
    <div style={{ position: 'relative', textAlign: 'center' }}>
      {loading && !hasCachedData && <LoadingOverlay image={coupon} message="Loading coupons..." />}
      <div style={{ opacity: 1 }}>
        <span className="coupon-count">{coupons}</span>
        <p className="coupon-label">
          {coupons === 1 ? 'coupon available' : 'coupons available'}
          {loading && hasCachedData && <span className="updating-indicator"> ↻</span>}
        </p>
        <button onClick={handleRedeemClick} disabled={loading || coupons <= 0}>
          Redeem Coupon
        </button>
      </div>
      
      {showSlider && (
        <RedeemSliderModal 
          onRedeem={handleConfirmRedeem}
          onClose={() => setShowSlider(false)}
        />
      )}
    </div>
  );
};

export default CouponsPanel;
